import type { Permission } from "./permissions"

export interface Role {
  id: "VIEWER" | "COMMENTER" | "EDITOR" | "MANAGER"
  title: string
  /**
   * I'm trying to move away from these and only read these -- I try to send to the server
   * the id above so the server can figure out which is the role. Also,
   * roles are stored as an array, and I want to try to separate the roles we have on the client
   * from their articulation in the database as a string in an array.
   *
   * For instance, we have to do this to find a selected role:
*     @selected={{
      find-by "officialId" @collaborator.attributes.roles.firstObject this.availableRoles
    }}

    * IMO, that's just too complex -- it's kinda makes it hard to do what the roles in an array are intended
    * to do and that's to let someone have multiple roles.
   */
  officialId: string
}

export const PLANBOOK_ROLES: Array<Role> = [
  { id: "VIEWER", title: "Can View", officialId: "planbook:is-viewer" },
  { id: "COMMENTER", title: "Can Comment", officialId: "planbook:is-commenter" },
  { id: "EDITOR", title: "Can Edit", officialId: "planbook:is-editor" },
  { id: "MANAGER", title: "Can Edit & Manage", officialId: "planbook:is-manager" },
]

export interface Planbook {
  id: string
  type: "planbook"
  attributes: PlanbookAttributes
  relationships: PlanbookRelationships
  meta: { permissions: Array<Permission> }
}

export interface PlanbookSummary {
  id: string
  type: "planbook-summary"
  attributes: PlanbookSummaryAttributes
  relationships: PlanbookRelationships
  meta: {}
}

interface PlanbookSummaryAttributes {
  title: string
  version: number
  cover: string
  years: Array<number>
  schoolYearId: string
  // ownerSummary -- not sure what this is.
  // I assume this is the right type for collaborators, but the person who first hits
  // a type error should double check this one.
  collaborators: Array<PlanbookCollaborator>
}

export interface PlanbookAttributes {
  title: string
  years: Array<number>
  /**
   * A planbook cover is either:
   * - One of our set backgrounds without a pattern (e.g. "gradient-blue-purple.svg")
   * - One of our set backgrounds with a pattern, separated by a colon (e.g. "gradient-blue-purple.svg:anchorsAway.svg")
   * - A custom image URL ("https://...").
   *
   * See utils/planbook-covers.ts for options.
   */
  cover: string
  schoolYearId: string
  collaborators: Array<PlanbookCollaborator>
  flags: PlanbookFlags
  calendar: PlanbookCalendar
  publicAccess: PlanbookPublicAccess
  recentStandardSetIds: Array<string>
  integrations: PlanbookIntegrations | null
  groupCalendarSubscriptions: Array<PlanbookGroupCalendarSubscription>
  deletedAt: string | null
  version: number
  _revision: number
  _revisedBySessionId: string | null
}

export interface PlanbookFlags {
  isImportingLessons: boolean | null
  isSameSchoolYearAsCopy: boolean | null
  hasNotFinishedOnboarding: boolean | null
  unifiedLessons: boolean | null
  isStarred: boolean | null
}

export interface PlanbookIntegrations {
  googleClassroom?: PlanbookIntegrationGoogleClassroom
}

export interface PlanbookIntegrationGoogleClassroom {
  authId: string
  authUserId: string
  timeZoneToPostIn: string
  hasNecessaryScopes: boolean
  integrationErrors: Array<IntegrationError>
}

interface IntegrationError {
  code: "PERMISSION_DENIED" | "FAILED_PRECONDITION" | "RESOURCE_EXHAUSTED" | "INTERNAL"
  message: string
  rawError: Array<object>
}

export interface PlanbookPublicAccess {
  /** the roles a non-logged in user can have ex "planbook:is-viewer" */
  roles: Array<string>
  /** refers to if link can be indexed by search engine */
  requiresLink: boolean
}

export interface PlanbookGroupCalendarSubscription {
  groupId: string
}

export interface PlanbookCalendar {
  datesOff: Array<string>
  dateAnnotations: Array<PlanbookDateAnnotation>
  dates: Array<PlanbookCalendarDateCustom | PlanbookCalendarDateDefault>
  version: number
}

type PlanbookCalendarDateCustomType = "planbook-date-custom"
type PlanbookCalendarDateDefaultType = "planbook-date-default"
type PlanbookCalendarDateType = PlanbookCalendarDateCustomType | PlanbookCalendarDateDefaultType

export interface PlanbookCalendarDate {
  id: string
  type: PlanbookCalendarDateType
  attributes: PlanbookCalendarDateCustomAttributes | PlanbookCalendarDateDefaultAttributes
}

export interface PlanbookCalendarDateCustom {
  id: string
  type: PlanbookCalendarDateCustomType
  attributes: PlanbookCalendarDateCustomAttributes
}

export interface PlanbookCalendarDateDefault {
  id: string
  type: PlanbookCalendarDateDefaultType
  attributes: PlanbookCalendarDateDefaultAttributes
}

interface PlanbookCalendarDateCustomAttributes {
  cardStackId: string | null
  dateString: string
}

interface PlanbookCalendarDateDefaultAttributes {
  cardStackId: string
  semesterId: string
  rotationId: string
}

// export interface PlanbookDateAnnotation {
//   id: string
//   type:
//     | "date-annotation-planbook-custom-single"
//     | "date-annotation-planbook-custom-range"
//     | "date-annotation-planbook-default"
//   attributes:
//     | PlanbookDateAnnotationDefault
//     | PlanbookDateAnnotationSingle
//     | PlanbookDateAnnotationDateRange
// }
export type PlanbookDateAnnotation =
  | PlanbookDateAnnotationDefault
  | PlanbookDateAnnotationSingle
  | PlanbookDateAnnotationDateRange

interface PlanbookDateAnnotationDefault {
  id: string
  type: "date-annotation-planbook-default"
  attributes: {
    semesterId: string
    rotationId: string
  }
}

export interface PlanbookDateAnnotationSingle {
  id: string
  type: "date-annotation-planbook-custom-single"
  attributes: {
    title: string
    description: string | null
    dateString: string
    isOff: boolean
    isPublic: boolean
  }
}

export interface PlanbookDateAnnotationDateRange {
  id: string
  type: "date-annotation-planbook-custom-range"
  attributes: {
    title: string
    description: string | null
    startDate: string
    endDate: string
    isOff: boolean
    isPublic: boolean
    publishedByGroupId: string | null
  }
}

export function isFromSubscribedCalendar(annotation: PlanbookDateAnnotation): boolean {
  return (
    annotation.type === "date-annotation-planbook-custom-range" &&
    annotation.attributes.publishedByGroupId !== null
  )
}

interface PlanbookRelationships {
  courses: { data: Array<CourseLinkage> }
  cardStackTemplates: { data: Array<CardStackTemplateLinkage> }
  classWebsites: { data: Array<ClassWebsiteLinkage> }
  deletedCourses: { data: Array<DeletedCourseLinkage> }
  owner: { data: { type: "user"; id: string } }
  planbookStandardsReport: { data: { type: "planbook-standards-report"; id: string } }
  rotationCalendar: { data: { type: "rotation-calendar"; id: string } }
}

interface CourseLinkage {
  type: "course"
  id: string
}

interface CardStackTemplateLinkage {
  type: "card-stack"
  id: string
}

interface ClassWebsiteLinkage {
  type: "class-website"
  id: string
}

interface DeletedCourseLinkage {
  type: "deleted-course"
  id: string
}

export type PlanbookRole =
  | "planbook:is-editor"
  | "planbook:is-manager"
  | "planbook:is-viewer"
  | "planbook:is-commenter"

export type PlanbookCollaborator =
  | PlanbookCollaboratorGroup
  | PlanbookCollaboratorUser
  | PlanbookCollaboratorEmail

export interface PlanbookCollaboratorEmail {
  id: string
  type: "planbook-collaborator-email"
  attributes: {
    roles: Array<PlanbookRole>
    email: string
    upgradeToRolesWhenPro: Array<PlanbookRole>
  }
}

export interface PlanbookCollaboratorGroup {
  id: string
  type: "planbook-collaborator-group"
  attributes: {
    roles: Array<PlanbookRole>
    groupId: string
  }
}

export interface PlanbookCollaboratorUser {
  id: string
  type: "planbook-collaborator-user"
  attributes: {
    roles: Array<PlanbookRole>
    userId: string
    upgradeToRolesWhenPro: Array<PlanbookRole>
  }
}
